import React, { MouseEvent, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import CartIcon from '../../../../ui/svg/shopping-cart/cart';
import TopMiniCartDropPanel from './drop-panel';
import { CLOSE_SEARCH_D_EVENT } from '../../search-desktop/ui/main';
import { CLOSE_HEADER_ACCOUNT_EVENT } from '../../account/ui/main';
import { IStoreProps } from '../index';
import { sendGAEvent } from '../../../../domain/marketing/gtm-provider';
import { mod } from '../../../../domain/math-provider';

export const UPDATE_MINI_CART_COUNT_EVENT = 'UPDATE_MINI_CART_COUNT_EVENT';
export const CLOSE_MINI_CART_EVENT = 'CLOSE_MINI_CART_EVENT';
export const CLOSE_NOTIFICATIONS_EVENT = 'CLOSE_NOTIFICATIONS_EVENT';
export const UPDATE_COUNT_VALUE_ONLY_EVENT = 'UPDATE_COUNT_VALUE_ONLY_EVENT';

interface ITopMiniCart {
    isMobile: boolean;
    storeProps: IStoreProps;
}

const TopMiniCart = (props: ITopMiniCart) => {

    const { isMobile, storeProps } = props;
    const { useAppSelector, rootActions } = storeProps;

    const dispatch = useDispatch();

    const isOpened = useAppSelector(store => store.root.isOpened);
    const count = useAppSelector(store => store.root.count);
    const selectedItemGuid= useAppSelector(store => store.root.selectedItemGuid);
    const cartData = useAppSelector(store => store.root.cartData);

    // ------------------ OPEN / CLOSE ----------------------
    const toggle = () => {

        dispatch(rootActions.main({
            isOpened: !isOpened,
            showNewUserPopup: false,
            isLoading: true,
        }));

        // close account and desktop search
        document.dispatchEvent(new CustomEvent(CLOSE_SEARCH_D_EVENT));
        document.dispatchEvent(new CustomEvent(CLOSE_HEADER_ACCOUNT_EVENT));
        document.dispatchEvent(new CustomEvent(CLOSE_NOTIFICATIONS_EVENT));

        if(!isOpened){
            sendGAEvent('Top Mini Cart', 'Open Panel');
        }
    };

    const close = useCallback(() => {
        dispatch(
            rootActions.main({
                isOpened: false,
                showNewUserPopup: false,
                isLoading: true,
            })
        )
    }, [dispatch, rootActions]);

    /**
     * handle click outside
     */
    useEffect(() => {

        document.addEventListener('click', close);

        return () => {
            document.removeEventListener('click', close);
        };
    }, [close]);

    /**
     * Update count from outside.
     */
    useEffect(() => {

        const updateCount = (evt: CustomEvent) => {

            const count = evt.detail || 0;

            dispatch(
                rootActions.main({
                    count,
                })
            );
        };

        // @ts-ignore
        document.addEventListener(UPDATE_COUNT_VALUE_ONLY_EVENT, updateCount);

        return () => {
            // @ts-ignore
            document.removeEventListener(UPDATE_COUNT_VALUE_ONLY_EVENT, updateCount);
        };
    }, [
        dispatch,
        rootActions,
    ]);

    /**
     * Close mini-cart from outside.
     */
    useEffect(() => {
        document.addEventListener(CLOSE_MINI_CART_EVENT, close);

        return () => {
            document.removeEventListener(CLOSE_MINI_CART_EVENT, close);
        };
    }, [dispatch, close]);

    const stopPropagation = (evt: MouseEvent) => {
        evt.stopPropagation();
    };

    useEffect(() => {

        const getNextGuid = (forward: boolean) => {
            const cartItems = cartData?.cartItems || [];
            if(cartItems.length <= 0) return null;

            if(!selectedItemGuid) return cartItems[0].cartItemGUID;

            const index = cartItems.findIndex(item => item.cartItemGUID === selectedItemGuid);
            if(index === -1) return cartItems[0].cartItemGUID;

            const nexIndex = mod(forward ? index + 1 : index - 1, cartItems.length);

            return cartItems[nexIndex].cartItemGUID;
        };

        const arrowDown = () => {
            const nextGuid = getNextGuid(true);
            if(!nextGuid) return;

            dispatch(
                rootActions.main({
                    selectedItemGuid: nextGuid,
                })
            );
        };

        const arrowUp = () => {
            const prevGuid = getNextGuid(false);
            if(!prevGuid) return;

            dispatch(
                rootActions.main({
                    selectedItemGuid: prevGuid,
                })
            );
        };

        const handleKeyDown = (evt: KeyboardEvent) => {

            if(!isOpened) return;

            if(evt.key === 'ArrowDown') {
                evt.preventDefault();
                evt.stopPropagation();
                arrowDown();
            }

            if(evt.key === 'ArrowUp') {
                evt.preventDefault();
                evt.stopPropagation();
                arrowUp();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        cartData?.cartItems,
        dispatch, rootActions,
        selectedItemGuid,
        isOpened,
    ]);

    // ------------------------------------------------------

    return (
        <div className={ `flex items-center relative ${ isMobile ? 'justify-center h-full mr-1 py-3' : '' }` } onClick={ stopPropagation }>
            <button
                type="button"
                onClick={toggle}
                className="flex items-center justify-center relative mr-4">

                <span className="sr-only">Open cart popup</span>

                <div
                    aria-hidden="true"
                    className="top-mini-cart-count absolute bg-blue-200 rounded-full flex items-center justify-center text-white">{count}</div>

                <CartIcon width="20" color="#F8F9FB" style={{
                    marginTop: '2px',
                }}/>

            </button>

            {isOpened && <TopMiniCartDropPanel storeProps={ storeProps } /> }
        </div>
    )
};

export default TopMiniCart;